import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { X, ArrowRight, Zap, CheckCircle2, Calendar, QrCode, Users, BarChart3, ShieldCheck, Smartphone } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const FEATURE_ITEMS = [
  { icon: Calendar, label: "Inscriptions en ligne" },
  { icon: QrCode, label: "QR Codes individuels" },
  { icon: Users, label: "Invitations numeriques" },
  { icon: ShieldCheck, label: "Controle des entrees" },
  { icon: Smartphone, label: "Gestion participants" },
  { icon: BarChart3, label: "Statistiques" },
];

function shouldShowPopup(campaign) {
  const freq = campaign?.popup_frequency || "once_per_day";
  const key = `glotech_popup_${campaign?.id || "default"}`;

  if (freq === "always") return true;

  if (freq === "once_per_session") {
    return !sessionStorage.getItem(key);
  }

  if (freq === "once") {
    return !localStorage.getItem(key);
  }

  // once_per_day or custom reappear_hours
  const dismissedUntil = localStorage.getItem(`${key}_until`);
  if (dismissedUntil && Date.now() < parseInt(dismissedUntil)) return false;
  return true;
}

function markPopupDismissed(campaign) {
  const freq = campaign?.popup_frequency || "once_per_day";
  const key = `glotech_popup_${campaign?.id || "default"}`;

  if (freq === "once") {
    localStorage.setItem(key, "1");
    return;
  }

  if (freq === "once_per_session") {
    sessionStorage.setItem(key, "1");
    return;
  }

  // once_per_day or custom hours
  const hours = campaign?.popup_reappear_hours || 24;
  const until = Date.now() + hours * 3600000;
  localStorage.setItem(`${key}_until`, String(until));
}

export default function PromotionalPopup() {
  const [visible, setVisible] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [closing, setClosing] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const [initialPath] = useState(location.pathname);

  // Ne pas afficher le popup pour les utilisateurs connectés (admins, org_admins, etc.)
  const isAuthenticated = !!localStorage.getItem("token");

  const dismiss = useCallback(() => {
    if (closing) return;
    setClosing(true);
    if (campaign) markPopupDismissed(campaign);
    setTimeout(() => setVisible(false), 300);
  }, [campaign, closing]);

  useEffect(() => {
    if (isAuthenticated) return; // Skip popup for authenticated users
    const fetchCampaign = async () => {
      try {
        const res = await fetch(`${API}/api/ad-campaigns/public?placement=popup`);
        if (!res.ok) return;
        const data = await res.json();
        if (data.length === 0) return;

        const c = data[0];

        // Check device restrictions
        const isMobile = window.innerWidth < 640;
        if (isMobile && c.popup_show_mobile === false) return;
        if (!isMobile && c.popup_show_desktop === false) return;

        // Check frequency
        if (!shouldShowPopup(c)) return;

        setCampaign(c);
        const delay = (c.popup_delay_seconds || 3) * 1000;
        setTimeout(() => setVisible(true), delay);

        // Track impression
        fetch(`${API}/api/ad-campaigns/public/${c.id}/impression`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ placement: "popup" }),
        }).catch(() => {});
      } catch {
        // No fallback - if API fails, don't show popup
      }
    };
    fetchCampaign();
  }, []);

  // Escape key
  useEffect(() => {
    if (!visible) return;
    const handleEsc = (e) => { if (e.key === "Escape") dismiss(); };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, [visible, dismiss]);

  if (!visible || !campaign) return null;

  const handleCTA = (url) => {
    if (campaign.id) {
      fetch(`${API}/api/ad-campaigns/public/${campaign.id}/click`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ placement: "popup" }),
      }).catch(() => {});
    }
    dismiss();
    if (url?.startsWith("/")) navigate(url);
    else if (url) window.open(url, "_blank");
  };

  const isMobile = typeof window !== "undefined" && window.innerWidth < 640;

  return (
    <div
      data-testid="promotional-popup-modal"
      className={`fixed inset-0 z-[9999] flex items-end sm:items-center justify-center transition-all duration-300 ${closing ? "opacity-0" : "opacity-100"}`}
      onClick={(e) => { if (e.target === e.currentTarget && campaign.popup_close_allowed !== false) dismiss(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />

      {/* Modal */}
      <div className={`relative w-full ${isMobile ? "max-h-[92vh] rounded-t-3xl" : "max-w-2xl rounded-2xl mx-4"} bg-[#0B0F19] border border-[#0099CC]/20 shadow-[0_20px_80px_rgba(0,153,204,0.15)] overflow-hidden transform transition-all duration-500 ${closing ? (isMobile ? "translate-y-full" : "scale-95 opacity-0") : (isMobile ? "translate-y-0" : "scale-100")}`}>

        {/* Top accent line */}
        <div className="h-1 w-full bg-gradient-to-r from-[#0099CC] via-[#10B981] to-[#0099CC]" />

        {/* Close button */}
        {campaign.popup_close_allowed !== false && (
          <button
            data-testid="close-popup-btn"
            onClick={dismiss}
            className="absolute top-5 right-5 z-10 text-[#64748b] hover:text-white bg-white/5 hover:bg-white/10 rounded-full p-2 transition-all"
            aria-label="Fermer"
          >
            <X className="h-5 w-5" />
          </button>
        )}

        <div className="p-6 sm:p-8 overflow-y-auto" style={{ maxHeight: isMobile ? "88vh" : "auto" }}>
          {/* Badge */}
          <div data-testid="popup-launch-banner" className="inline-flex items-center gap-2 bg-[#10B981]/10 border border-[#10B981]/30 text-[#10B981] font-mono text-xs px-3 py-1.5 rounded-full mb-5">
            <Zap className="h-3.5 w-3.5" />
            <span className="tracking-wider uppercase">{campaign.subtitle || "ACCES GRATUIT"}</span>
          </div>

          {/* Title */}
          <h2 className="text-2xl sm:text-3xl font-extrabold tracking-tight text-white mb-3 leading-tight">
            {campaign.title}
          </h2>

          {/* Description */}
          <p className="text-[#94A3B8] text-sm sm:text-base leading-relaxed mb-6 max-w-lg">
            {campaign.description?.split("\n")[0]}
          </p>

          {/* Feature grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5 mb-6">
            {FEATURE_ITEMS.map(({ icon: Icon, label }) => (
              <div key={label} className="flex items-center gap-2 bg-[#151B2B]/80 border border-[#2A3441] rounded-xl px-3 py-2.5 text-xs text-[#CBD5E1]">
                <Icon className="h-4 w-4 text-[#0099CC] shrink-0" />
                <span>{label}</span>
              </div>
            ))}
          </div>

          {/* Free access highlight */}
          <div className="flex items-center gap-2 bg-[#10B981]/5 border border-[#10B981]/20 rounded-xl px-4 py-3 mb-6">
            <CheckCircle2 className="h-5 w-5 text-[#10B981] shrink-0" />
            <span className="text-sm text-[#10B981] font-medium">
              {campaign.description?.split("\n").pop() || "Acces gratuit pendant la periode de lancement"}
            </span>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="popup-primary-cta"
              onClick={() => handleCTA(campaign.cta_url || "/evenements")}
              className="flex-1 bg-gradient-to-r from-[#0099CC] to-[#0077A3] hover:from-[#00AADB] hover:to-[#0088B8] text-white font-bold py-3.5 px-6 rounded-xl shadow-[0_4px_20px_rgba(0,153,204,0.3)] transition-all hover:-translate-y-0.5 active:translate-y-0 flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              {campaign.cta_text || "Demander mon acces gratuit"}
              <ArrowRight className="h-4 w-4" />
            </button>
            <button
              data-testid="popup-secondary-cta"
              onClick={() => handleCTA("/evenements")}
              className="flex-1 bg-[#151B2B] hover:bg-[#1C2333] text-[#94A3B8] hover:text-white border border-[#2A3441] hover:border-[#4A5568] font-semibold py-3.5 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-sm sm:text-base"
            >
              Decouvrir la plateforme
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
